/**
 * Query Tracker.
 *
 * Intercepts fetch() calls to the API and tracks their status/timing.
 * Only active in development mode. Components consume the store via
 * React's useSyncExternalStore hook.
 */

export type QueryStatus = "pending" | "loaded" | "error";

export interface SqlQuery {
  sql: string;
  params: Record<string, unknown> | null;
  durationMs: number;
  rowCount: number;
  cached: boolean;
}

export interface TrackedQuery {
  id: number;
  url: string;
  method: string;
  status: QueryStatus;
  startTime: number;
  networkMs: number | null;
  serverMs: number | null;
  httpStatus: number | null;
  sqlQueries: SqlQuery[];
}

type Listener = () => void;

let nextId = 1;
let queries: TrackedQuery[] = [];
let listeners: Set<Listener> = new Set();
let installed = false;
let currentPath = "";

function notify(): void {
  listeners.forEach((listener) => listener());
}

function getApiBaseUrl(): string {
  return process.env.REACT_APP_API_BASE_URL || "http://localhost:4000/api";
}

function isApiCall(url: string): boolean {
  const base = getApiBaseUrl();
  return url.startsWith(base) || url.startsWith("/api");
}

function stripBase(url: string): string {
  const base = getApiBaseUrl();
  if (url.startsWith(base)) return url.slice(base.length);
  if (url.startsWith("/api")) return url.slice(4);
  return url;
}

/**
 * Install the fetch interceptor. Must be called before React renders.
 * Only installs in development mode.
 */
export function install(): void {
  if (installed) return;

  installed = true;
  const originalFetch = window.fetch;

  // Track React Router navigation to clear queries on page change
  currentPath = window.location.pathname;
  const checkPath = (): void => {
    if (window.location.pathname !== currentPath) {
      currentPath = window.location.pathname;
      queries = [];
      notify();
    }
  };

  // Listen for popstate (back/forward) and periodic check for pushState
  window.addEventListener("popstate", checkPath);
  setInterval(checkPath, 500);

  window.fetch = async function trackedFetch(
    input: RequestInfo | URL,
    init?: RequestInit,
  ): Promise<Response> {
    const url =
      typeof input === "string"
        ? input
        : input instanceof URL
          ? input.toString()
          : input.url;

    if (!isApiCall(url)) {
      return originalFetch(input, init);
    }

    const entry: TrackedQuery = {
      id: nextId++,
      url: stripBase(url),
      method: (init?.method || "GET").toUpperCase(),
      status: "pending",
      startTime: performance.now(),
      networkMs: null,
      serverMs: null,
      httpStatus: null,
      sqlQueries: [],
    };

    queries = [...queries, entry];
    notify();

    try {
      const response = await originalFetch(input, init);
      const elapsed = performance.now() - entry.startTime;

      // Clone to read duration_ms and _debug without consuming the body
      let serverMs: number | null = null;
      let sqlQueries: SqlQuery[] = [];
      try {
        const clone = response.clone();
        const json = await clone.json();
        if (json && typeof json.duration_ms === "number") {
          serverMs = json.duration_ms;
        }
        if (json?._debug?.queries && Array.isArray(json._debug.queries)) {
          sqlQueries = json._debug.queries;
        }
      } catch {
        // Not JSON or parsing failed — that's fine
      }

      const updated: TrackedQuery = {
        ...entry,
        status: response.ok ? "loaded" : "error",
        networkMs: Math.round(elapsed),
        serverMs,
        httpStatus: response.status,
        sqlQueries,
      };

      queries = queries.map((q) => (q.id === entry.id ? updated : q));
      notify();

      return response;
    } catch (err) {
      const elapsed = performance.now() - entry.startTime;

      const updated: TrackedQuery = {
        ...entry,
        status: "error",
        networkMs: Math.round(elapsed),
        httpStatus: null,
      };

      queries = queries.map((q) => (q.id === entry.id ? updated : q));
      notify();

      throw err;
    }
  };
}

// useSyncExternalStore interface
export function subscribe(listener: Listener): () => void {
  listeners.add(listener);
  return () => listeners.delete(listener);
}

export function getSnapshot(): TrackedQuery[] {
  return queries;
}

export function clear(): void {
  queries = [];
  notify();
}
